import React, { useState, useCallback, useEffect, useMemo } from "react";
import {useDispatch, useSelector} from "react-redux";
import StockChart from "../../charts/StockChart";
import {fetchTimeSeriesData} from "../../api/timeseriesApi";
import {selectManyTimeSeriesData} from "../../store/slices/timeseries.slice";
import {modernFintechPalette} from "../../charts/nivo/utils";
import {toRGB} from "../../utils/colorUtils";

const formatSeries = (data)=>{
    return data.map((item)=>{
        const row = [item.timestamp, item['adj close']];
        if (item.hasOwnProperty('snapshot')){
            row.push(item.snapshot);
        }
        return row;
    });

}



const formatChartData = (timeseriesData, colorMapping, labelMapping, yMinorGrid)=>{
    const plotData = [];
    timeseriesData.forEach(item=>{
        const [symbol, data] = [item.symbol, item.data];
        const seriesRows = formatSeries(data);
        plotData.push({
            name: labelMapping ? labelMapping[symbol] : symbol,
            data: seriesRows,
            type: 'line',
            stroke: colorMapping ? colorMapping?.[symbol] : modernFintechPalette[timeseriesData.indexOf(item)],
            format: '%',
            thickness: 2,
            hasSnapshot: seriesRows[0].length === 3,
            callbackOnSnapshot: item?.callbackOnSnapshot
        });
    });
    return [
        {
            plot: 0,
            data: plotData,
            yScale: 'percent',
            format: '%',
            legend: {postfix: '%', precision: 2, format: '%'},
            yMinorGrid: yMinorGrid

        }
    ]
}


/**
 *
 * @param symbols {Array} - List of ticker symbol or portfolio uuids
 * @param startDate {string, optional} - YYYY-MM-DD formatted date
 * @param endDate {string, optional} - YYYY-MM-DD formatted date
 * @param colorMapping {Object, optional} - Mapping of symbols to colors
 * @param labelMapping {Object, optional} - Mapping of ids to labels. This can be used for just a few items in the
 * series that might need a custom label such as portfolio uuids
 * @param initialSeries {Array[Array], optional} - Arrays of rows representing the initial series data. This
 * includes both individual and multiple series. For all series there must be for each row at minimum a timestamp
 * and value ex [<timestamp>, <value>], for portfolio series there will be an additional item for each row that \
 * is an object containing that days 'snapshot' for custom callback use within the chart.
 * @param customSnapshotCallback {function, optional} - Callback function for custom snapshot data
 * @param ref {object, optional} - Reference to the chart component
 * @param stockChartProps {object, optional} - Additional props to pass to the StockChart component
 * @param height {number, optional} - Height of the chart
 * @param yMinorGrid {boolean, optional} - Show minor grid lines on y-axis
 * @returns {null|JSX.Element}
 * @constructor
 */
const MultiStockChart = ({symbols, startDate, endDate, colorMapping, labelMapping,
                             initialSeries, customSnapshotCallback, ref, stockChartProps,
                             height=600, heightOffset=50, yMinorGrid=false})=>{

    const dispatch = useDispatch();
    const timeSeriesData = useSelector(state=>selectManyTimeSeriesData(state, symbols));
    const { loadingSlice, sliceError } = useSelector(state=>state.timeSeries);
    const [mergedData, setMergedData] = useState([]);
    useEffect(() => {
        if (initialSeries) {
            setMergedData(initialSeries);
        }
    }, [initialSeries]);
    useEffect(()=>{
        const fetchData = async ()=>{
            await dispatch(fetchTimeSeriesData({symbols, startDate, endDate}))
        }

        if (!loadingSlice && !sliceError && timeSeriesData.length !== symbols.length){
            fetchData();
        }

    });
    return !timeSeriesData ? null : (
        <div style={{height: height, width: '100%'}}>
            <StockChart
                height={height-heightOffset}
                dataSeries={formatChartData([...mergedData, ...timeSeriesData], colorMapping, labelMapping, yMinorGrid)}
                customSnapshotCallback={customSnapshotCallback}
                ref={ref}
                {...stockChartProps? stockChartProps : {}}
            />
        </div>
    )


}

export default MultiStockChart;